import { useCallback, useEffect, useMemo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiAlert,
  MiPlaceholderArea,
  MiProfilePicture,
} from "@miplatform/components";
import { HTTP_STATUSES } from "@miplatform/constants";
import {
  ButtonVariants,
  CalendarEventType,
  ITransferListItem,
  IUserListItemResponse,
  PlatformModules,
} from "@miplatform/types";
import { simpleSort } from "@miplatform/utils";
import { mdiPlus } from "@mdi/js";
import { TransferList } from "@mibuildweb/components";
import { useRoles, useUsers } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { CALENDAR_PAGE_CONFIG } from "../../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";

const LABEL_MAP = {
  schedulerUsers: "Calendar Users",
  assignableUsers: "Assignable Users",
};

export const CalendarUsersTabView = ({
  form,
  extraTabProps: {
    isReadOnly,
    usersType = "schedulerUsers",
    eventType = CalendarEventType.Order,
    onAddWorkTemplateBase,
  } = {},
}: CalendarUsersTabViewProps) => {
  const theme = useTheme();
  const { getValues, setValue, watch } = form;
  const users = watch(usersType) || [];
  const workTemplateBases = getValues("workTemplateBases") || [];
  const assignableUsers = useWatch({
    control: form.control,
    name: "assignableUsers",
  });
  const { users: allUsers, setUsers, getUsersByWorkPermissions } = useUsers();
  const { roles, getRoles, setRoles } = useRoles();

  const getData = async () => {
    const permissionsEntity = CALENDAR_PAGE_CONFIG[eventType].permissions;
    const workPermissions =
      usersType === "schedulerUsers"
        ? [
            permissionsEntity.ASSIGN,
            permissionsEntity.CREATE,
            permissionsEntity.UPDATE,
            permissionsEntity.SCHEDULE,
          ]
        : [permissionsEntity.PERFORM_WORK];
    const workTemplateBases = getValues("workTemplateBases") || [];

    const promises = [
      getUsersByWorkPermissions({
        params: {
          workPermissions,
          entityType: CALENDAR_PAGE_CONFIG[eventType].workEntity,
          ...(workTemplateBases.length > 0 && {
            entitiesIds: workTemplateBases.map(({ id }) => id),
          }),
        },
      }),
      getRoles(),
    ];
    const [usersResponse, rolesResponse] = await Promise.all(promises);

    if (usersResponse.status === HTTP_STATUSES.OK) {
      setUsers(usersResponse.data);
    }

    if (rolesResponse.status === HTTP_STATUSES.OK) {
      setRoles(rolesResponse.data);
    }
  };

  const handleAddItem = (key: string) => {
    const selectedUser = allUsers.find((d) => d.id === key);

    if (!selectedUser) {
      return;
    }
    const updatedUsers = [...users, selectedUser];

    setValue(usersType, updatedUsers, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveItem = (key: string) => {
    const updatedUsers = users.filter((d) => d.id !== key);
    setValue(usersType, updatedUsers, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const [userGroupsDictionary, usersDictionary] = useMemo(() => {
    const userGroupsDict: Record<
      string,
      {
        key: string;
        name: string;
        description?: string;
        users: Set<string>;
      }
    > = {};
    const usersDict: Record<string, IUserListItemResponse> = {};

    roles.forEach((role) => {
      userGroupsDict[role.id] = {
        key: role.id,
        name: role.name,
        description: role.description,
        users: new Set<string>(),
      };
    });

    allUsers.forEach((user) => {
      usersDict[user.id] = user;
      user.roles?.forEach((role) => {
        if (userGroupsDict[role.id]) {
          userGroupsDict[role.id].users.add(user.id);
        }
      });
    });

    return [userGroupsDict, usersDict];
  }, [roles, allUsers]);

  const groupByMapper = useCallback(
    (items: ITransferListItem[]) => {
      const groupedItems: { [key: string]: ITransferListItem[] } = {};
      const groupsDictionaryValues = Object.values(userGroupsDictionary);
      items.forEach((item) => {
        groupsDictionaryValues.forEach((group) => {
          if (group.users.has(item.key)) {
            const groupKey = group ? group.key : "ungrouped";

            if (!groupedItems[groupKey]) {
              groupedItems[groupKey] = [];
            }
            groupedItems[groupKey].push(item);
          }
        });
      });

      return groupedItems;
    },
    [userGroupsDictionary]
  );

  const getGroupByInfo = useCallback(
    (key: string) => {
      const group = userGroupsDictionary[key] || {
        key,
        name: "No Group Found",
        description: "",
      };
      return {
        name: group?.name,
        description: group?.description,
        key: group.key,
      };
    },
    [userGroupsDictionary]
  );

  const availableUsers = useMemo(
    () =>
      allUsers.map((i) => ({
        key: i.id,
        title: i.name,
      })) as ITransferListItem[],
    [allUsers]
  );

  const selectedUsers = useMemo(
    () =>
      users
        .sort((a, b) => simpleSort(a.name, b.name))
        .map((i) => ({ key: i.id, title: i.name })) as ITransferListItem[],
    [users]
  );

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const workCapacities = getValues("workCapacities");
    Object.keys(workCapacities).forEach((userId) => {
      if (!assignableUsers.some(({ id }) => id === userId)) {
        delete workCapacities[userId];
      }
    });
    setValue("workCapacities", workCapacities);
  }, [assignableUsers]);

  const renderUserTitle = (item: ITransferListItem) => {
    const user = usersDictionary[item.key];

    if (!user) {
      return null;
    }

    return (
      <Box display="flex" gap={2} alignItems="center">
        <MiProfilePicture
          name={user.name}
          size={20}
          fontSize={12}
          sx={{
            borderRadius: "4px",
            minWidth: 20,
            backgroundColor: theme.palette.primary.main,
          }}
        />
        <Typography
          variant="HD2"
          color={theme.palette.primary.main}
          sx={{ whiteSpace: "nowrap" }}
        >
          {user.name}
        </Typography>
      </Box>
    );
  };

  return (
    <ConfigAccordionHeader title={LABEL_MAP[usersType]}>
      {workTemplateBases.length === 0 ? (
        <MiPlaceholderArea
          text={`No Attached ${CALENDAR_PAGE_CONFIG[eventType].entityNamePlural}`}
          helperText={`Tip: attach ${CALENDAR_PAGE_CONFIG[
            eventType
          ].entityNamePlural.toLowerCase()} to see them here.`}
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            height: "100%",
            minHeight: 450,
          }}
          buttons={
            !isReadOnly
              ? [
                  {
                    title: `Add ${CALENDAR_PAGE_CONFIG[eventType].entityNamePlural}`,
                    startIcon: mdiPlus,
                    variant: ButtonVariants.Contained,
                    onClick: onAddWorkTemplateBase,
                  },
                ]
              : []
          }
        />
      ) : (
        <>
          <TransferList
            availableTitle="Available Roles & Users"
            availableItems={availableUsers}
            selectedTitle="Selected Users"
            emptyAvailableListMessage="There are no available items to select."
            emptySelectedListMessage="Easily add users to this calendar from the list on your left"
            selectedItems={selectedUsers}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
            groupBy="key"
            disabled={isReadOnly}
            groupByMapper={groupByMapper}
            getGroupByInfo={getGroupByInfo}
            renderItemTitle={renderUserTitle}
          />
          <MiAlert
            description={
              usersType === "schedulerUsers"
                ? "Select which users will be managing work and members on this calendar."
                : "Select which users can be assigned work on this calendar."
            }
            sx={{ mt: 2, py: 0, px: 2 }}
          />
        </>
      )}
    </ConfigAccordionHeader>
  );
};

interface CalendarUsersTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    module?: PlatformModules;
    eventType?: CalendarEventType;
    usersType?: "schedulerUsers" | "assignableUsers";
    onAddWorkTemplateBase?: () => void;
  };
}
